import React from 'react'
import { Select } from '@/components/ui'
import { FieldGroup, LabeledToggleRow, SettingsPane, ToggleList } from '../organization-ui'
import type { OrganizationSettingsFormProps } from '../settings/organizationSettingsForm.types'

/**
 * Chat and channel policy.
 *
 * Direct-message rules hang off `directMessagesEnabled`: when it is off, every
 * per-audience toggle below is moot, so they are disabled rather than hidden —
 * an admin should be able to see the policy they will get back when they turn
 * one-to-one chat on again.
 */
export const ChatPane: React.FC<OrganizationSettingsFormProps> = ({
  setValue,
  watch,
  disabled = false,
}) => {
  const directMessagesEnabled = watch('chatSettings.directMessagesEnabled') ?? true

  return (
    <SettingsPane
      title="Chat & channels"
      description="Set who can start direct conversations, create company channels, and send voice notes."
    >
      <FieldGroup label="Direct messages">
        <ToggleList>
          <LabeledToggleRow
            label="Enable one-to-one chat"
            description="When off, direct and group direct conversations are unavailable across the organization."
            checked={directMessagesEnabled}
            disabled={disabled}
            onChange={(checked) =>
              setValue('chatSettings.directMessagesEnabled', checked, { shouldDirty: true })
            }
          />
          <LabeledToggleRow
            label="Management can message staff"
            description="Allow management-level users to start direct conversations with staff."
            checked={watch('chatSettings.allowManagementToStaffDirectMessages') ?? true}
            disabled={disabled || !directMessagesEnabled}
            onChange={(checked) =>
              setValue('chatSettings.allowManagementToStaffDirectMessages', checked, {
                shouldDirty: true,
              })
            }
          />
          <LabeledToggleRow
            label="Allow staff-to-staff chat"
            description="Disabled by default. Enable it only when staff may start direct conversations with each other."
            checked={watch('chatSettings.allowStaffToStaffDirectMessages') ?? false}
            disabled={disabled || !directMessagesEnabled}
            onChange={(checked) =>
              setValue('chatSettings.allowStaffToStaffDirectMessages', checked, {
                shouldDirty: true,
              })
            }
          />
          <LabeledToggleRow
            label="Allow staff-to-management chat"
            description="Disabled by default. Enable it when staff may start direct conversations with management."
            checked={watch('chatSettings.allowStaffToManagementDirectMessages') ?? false}
            disabled={disabled || !directMessagesEnabled}
            onChange={(checked) =>
              setValue('chatSettings.allowStaffToManagementDirectMessages', checked, {
                shouldDirty: true,
              })
            }
          />
        </ToggleList>
      </FieldGroup>

      <FieldGroup label="Reach" hint="How far a member may look when starting a conversation">
        <div className={!directMessagesEnabled ? 'opacity-55' : undefined}>
          <Select
            label="One-to-one chat scope"
            value={watch('chatSettings.directMessageScope') ?? 'within_scope'}
            disabled={disabled || !directMessagesEnabled}
            onChange={(value) =>
              setValue(
                'chatSettings.directMessageScope',
                value as 'within_scope' | 'all_organization_members',
                { shouldDirty: true },
              )
            }
            options={[
              {
                value: 'within_scope',
                label: 'Within their scope',
                description: 'Only members who share an active company can be messaged.',
              },
              {
                value: 'all_organization_members',
                label: 'All organization members',
                description: 'Any eligible organization member can be messaged.',
              },
            ]}
          />
        </div>
      </FieldGroup>

      <FieldGroup label="Channels">
        <ToggleList>
          <LabeledToggleRow
            label="Allow all roles to create channels"
            description="When off, only the organization owner and management-level roles can create channels."
            checked={watch('chatSettings.allowAllRolesToCreateChannels') ?? false}
            disabled={disabled}
            onChange={(checked) =>
              setValue('chatSettings.allowAllRolesToCreateChannels', checked, {
                shouldDirty: true,
              })
            }
          />
          <LabeledToggleRow
            label="Allow voice notes"
            description="Show the voice-note recorder in permitted direct messages and channels."
            checked={watch('chatSettings.allowVoiceNotes') ?? false}
            disabled={disabled}
            onChange={(checked) =>
              setValue('chatSettings.allowVoiceNotes', checked, { shouldDirty: true })
            }
          />
        </ToggleList>
      </FieldGroup>
    </SettingsPane>
  )
}

ChatPane.displayName = 'ChatPane'
